import { Divider, Group, Input, Slider, Switch } from '@byecode/ui'
import { FilterBlockMode } from '@lighthouse/core'
import { Group4ByecodeUi, ListItem4ByecodeUi, ModeSegmented } from '@lighthouse/shared'
import React, { useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AnchorSetting } from '@/components/AnchorSetting'
import { InheritLabel } from '@/components/InheritLabel'
import { DesignSetting, FontSetting, getBlockAndPageDesignLimit, getBlockInteractionDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { useSetExtendsKeys } from '@/components/StyleSetting/hooks/useMouseEventDistance'
import { InteractionSetting } from '@/components/StyleSetting/InteractionSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'
import { TABS } from './constants'
import { FilterList } from './FilterList'

const SCxHeader = styled.div`
    padding: 12px 16px;
`

const SCxPopupTitle = styled.div`
    margin-bottom: 8px;
    height: 36px;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--color-black);
`

interface FilterSettingProps {
    blockId: string
}

export const FilterSetting: React.FC<FilterSettingProps> = ({ blockId }) => {
    const { control } = useFormContext()
    const mode = useWatch({ control, name: 'mode' })
    const showLabel = useWatch({ control, name: 'showLabel' })
    const theme = useWatch({ control, name: 'breakPoint.design.theme' })
    const { handleSetBreakKey } = useSetExtendsKeys()
    const interactionProps = getBlockInteractionDesignLimit('filter')
    const styleProps = getBlockAndPageDesignLimit('filter', { design: { enableLabelColor: mode === FilterBlockMode.tile } })
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)
    const { t } = useTranslation()
    const designAfterSlot = useMemo(() => {
        return interactionProps && <InteractionSetting {...interactionProps} />
    }, [interactionProps])

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <FontSetting {...styleProps.font} />
                    <Divider />
                    <Group label="样式">
                        <DesignSetting
                            blockType="filter"
                            colorLabel="文本颜色"
                            theme={theme}
                            {...styleProps.design}
                            afterSlot={designAfterSlot}
                        />
                    </Group>
                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <SCxHeader>
                        <SCxPopupTitle>筛选模式</SCxPopupTitle>
                        <Controller
                            control={control}
                            name="mode"
                            render={({ field: { value, onChange } }) => <ModeSegmented options={TABS} value={value} onChange={onChange} />}
                        />
                    </SCxHeader>
                    <Divider color="var(--color-gray-200)" />
                    <Group4ByecodeUi label="筛选项">
                        <FilterList blockId={blockId} />
                    </Group4ByecodeUi>

                    {mode === FilterBlockMode.tile && (
                        <>
                            <Divider color="var(--color-gray-200)" />
                            <Group4ByecodeUi label="设计">
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <div>显示标题</div>
                                    <Controller
                                        control={control}
                                        name="showLabel"
                                        render={({ field: { value, onChange } }) => {
                                            return <Switch checked={value} onChange={onChange} />
                                        }}
                                    />
                                </ListItem4ByecodeUi>
                                {showLabel && (
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <div>标题宽度</div>
                                        <Controller
                                            control={control}
                                            name="labelWidth"
                                            render={({ field }) => (
                                                <Slider
                                                    min={1}
                                                    max={400}
                                                    step={1}
                                                    value={field.value ?? 80}
                                                    onChange={field.onChange}
                                                    style={{ width: 180 }}
                                                    inputProps={{
                                                        onFocus: e => {
                                                            e.target.select()
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </ListItem4ByecodeUi>
                                )}
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <InheritLabel label="换行" name="breakPoint.layout.wrap" />
                                    <Controller
                                        control={control}
                                        name="breakPoint.layout.wrap"
                                        render={({ field }) => (
                                            <Switch
                                                checked={field.value}
                                                onChange={ev => {
                                                    field.onChange(ev.target.checked)
                                                    handleSetBreakKey('breakPoint.layout.wrap')
                                                }}
                                            />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <div>全选的文案</div>
                                    <Controller
                                        control={control}
                                        name="noFilterLabel"
                                        render={({ field }) => (
                                            <Input
                                                style={{ width: 180 }}
                                                placeholder={t('all')}
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                            </Group4ByecodeUi>
                        </>
                    )}
                    {/* FilterWay */}
                    <Divider color="var(--color-gray-200)" />
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && <AnimationSetting />}
        </>
    )
}
